import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Code2, Sparkles, RefreshCw } from "lucide-react";
import { StatusPill, fmtDate } from "./langStatus";
import { TemplateTranslationModal, DOC_FIELD_LABELS } from "./TemplateTranslationModal";

export function TemplateTranslationView({ group, tplKey, tplLabel, lang, langName, onChanged, fieldLabels }) {
  const labels = fieldLabels || DOC_FIELD_LABELS;
  const [d, setD] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const isEmail = group === "emails";

  const load = async () => {
    try {
      const r = await api.get(`/templates/i18n/${group}/${tplKey}/${lang}`);
      setD(r.data);
      if (isEmail && r.data.fields?.body_html) {
        const p = await api.get(`/templates/emails/${tplKey}/preview`, { params: { lang } });
        setPreview(p.data);
      } else setPreview(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { setD(null); load(); }, [group, tplKey, lang]); // eslint-disable-line

  const translate = async () => {
    let force = false;
    if (d?.reviewed) {
      if (!window.confirm("Esta traducción fue modificada manualmente. ¿Quieres sustituirla por una nueva traducción automática?")) return;
      force = true;
    }
    setBusy(true);
    try {
      const r = await api.post(`/templates/i18n/${group}/${tplKey}/${lang}/translate`, { force });
      const res = r.data.result || {};
      if (res.errors?.length) toast.error(`Traducción incompleta: ${res.errors.map((e) => e.error).join(" · ")}`);
      else toast.success(`Traducción ${langName} actualizada con OpenAI`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!d) return <div className="ds-card p-5 text-sm text-[#525252]" data-testid="tpl-i18n-loading">Cargando traducción…</div>;

  const st = { status: d.status, reviewed: d.reviewed };
  const translateLabel = d.status === "missing" ? "Traducir con OpenAI" : d.status === "outdated" ? "Actualizar traducción" : "Regenerar con OpenAI";
  const bodyText = d.fields?.body || "";
  const hasHtml = isEmail && d.master_fields?.body_html;

  return (
    <div className="ds-card p-5 space-y-4" data-testid="tpl-i18n-view">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#6E8B3D]">// {tplLabel}</div>
          <div className="font-display font-bold text-xl mt-1">Versión {langName}</div>
          <p className="text-xs text-[#525252] mt-1">Traducción de la plantilla maestra en español. El HTML y las variables se mantienen intactos.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => setOpen(true)} data-testid="tpl-i18n-open-modal"
            className="border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-xs font-mono uppercase inline-flex items-center gap-1">
            <Code2 size={12} /> {hasHtml ? "Ver / editar HTML" : "Ver / editar textos"}
          </button>
          <button onClick={translate} disabled={busy} data-testid="tpl-i18n-translate"
            className="bg-[#6E8B3D] hover:bg-[#556C2E] disabled:bg-[#A8A8A0] text-white px-3 py-2 text-xs font-mono uppercase inline-flex items-center gap-1">
            {busy ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />} {busy ? "Traduciendo…" : translateLabel}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Meta label="Estado"><StatusPill st={st} testid="tpl-i18n-status" /></Meta>
        <Meta label="Última actualización"><span data-testid="tpl-i18n-updated">{fmtDate(d.updated_at)}</span></Meta>
        <Meta label="Origen"><span data-testid="tpl-i18n-mode">{d.reviewed ? "Revisada manualmente" : d.status === "missing" ? "—" : `Automática (OpenAI${d.model ? ` · ${d.model}` : ""})`}</span></Meta>
        <Meta label="Idioma"><span className="font-mono">{lang}</span></Meta>
      </div>
      {d.last_error && <div className="border border-red-300 bg-red-50 text-red-700 text-xs px-3 py-2" data-testid="tpl-i18n-error">{d.last_error}</div>}

      {d.status === "missing" ? (
        <div className="border border-dashed border-[#B5B5B5] bg-[#FAFAF7] p-6 text-center text-sm text-[#525252]" data-testid="tpl-i18n-empty">
          Esta plantilla todavía no está traducida al {langName}. Pulsa <b>Traducir con OpenAI</b> o escribe la traducción desde <b>Ver / editar</b>.
        </div>
      ) : isEmail ? (
        <>
          {d.master_fields?.subject !== undefined && (
            <Meta label="Asunto traducido"><div className="font-mono text-sm bg-white border border-[#E5E5E0] px-3 py-2" data-testid="tpl-i18n-subject">{d.fields.subject || "—"}</div></Meta>
          )}
          {hasHtml ? (
            <Meta label="Previsualización">
              <div className="border border-[#E5E5E0] bg-white overflow-hidden h-64 relative" data-testid="tpl-i18n-preview">
                {preview?.body_html ? (
                  <iframe title="preview" srcDoc={preview.body_html} className="w-[200%] h-[200%] origin-top-left scale-50 pointer-events-none bg-white" />
                ) : <div className="p-4 text-xs text-[#525252]">Generando previsualización…</div>}
              </div>
            </Meta>
          ) : (
            <Meta label="Mensaje"><div className="text-sm whitespace-pre-wrap bg-white border border-[#E5E5E0] px-3 py-2 max-h-48 overflow-hidden" data-testid="tpl-i18n-body">{bodyText}</div></Meta>
          )}
        </>
      ) : (
        <div className="space-y-2">
          {Object.entries(d.fields).map(([f, v]) => (
            <Meta key={f} label={labels[f] || f}>
              <div className="text-sm bg-white border border-[#E5E5E0] px-3 py-2 line-clamp-2" data-testid={`tpl-i18n-doc-${f}`}>
                {Array.isArray(v) ? v.join(" · ") : group === "public" ? (v || "").split("\n").join(" · ") : v || <span className="text-[#A8A8A0]">— sin traducir —</span>}
              </div>
            </Meta>
          ))}
        </div>
      )}

      {open && (
        <TemplateTranslationModal
          group={group} tplKey={tplKey} tplLabel={tplLabel} lang={lang} langName={langName} detail={d} fieldLabels={fieldLabels}
          onClose={() => setOpen(false)}
          onSaved={async () => { setOpen(false); await load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function Meta({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">{label}</div>
      {children}
    </div>
  );
}
